import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Lock, Loader2, MapPin, ArrowRight, Gift, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useCart } from '@/context/CartContext';
import {
  updateCartCustomer,
  selectShippingRate,
  placeOrder,
} from '@/lib/api/endpoints';

const formatPrice = (paiseStr, currency = 'INR') => {
  const rupees = parseInt(paiseStr || '0', 10) / 100;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(rupees);
};

// Build Your Own Box items carry their flavor selection back from the
// Store API under extensions.raretto_build_box.flavors.
const buildBoxFlavors = (item) => item.extensions?.raretto_build_box?.flavors;

const initialForm = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  address: '',
  address2: '',
  city: '',
  state: '',
  pin: '',
  country: 'IN',
};

// The Store API validates `state` against this exact list of WooCommerce
// state codes for India — anything else (a city name, a typo, a state
// spelled slightly differently) 400s the whole update-customer/checkout
// call. A free-text input can't guarantee a match, so we constrain it to
// a dropdown instead.
const INDIA_STATES = [
  ['AN', 'Andaman and Nicobar Islands'],
  ['AP', 'Andhra Pradesh'],
  ['AR', 'Arunachal Pradesh'],
  ['AS', 'Assam'],
  ['BR', 'Bihar'],
  ['CH', 'Chandigarh'],
  ['CT', 'Chhattisgarh'],
  ['DD', 'Daman and Diu'],
  ['DH', 'Dadra and Nagar Haveli and Daman and Diu'],
  ['DL', 'Delhi'],
  ['DN', 'Dadra and Nagar Haveli'],
  ['GA', 'Goa'],
  ['GJ', 'Gujarat'],
  ['HP', 'Himachal Pradesh'],
  ['HR', 'Haryana'],
  ['JH', 'Jharkhand'],
  ['JK', 'Jammu and Kashmir'],
  ['KA', 'Karnataka'],
  ['KL', 'Kerala'],
  ['LA', 'Ladakh'],
  ['LD', 'Lakshadweep'],
  ['MH', 'Maharashtra'],
  ['ML', 'Meghalaya'],
  ['MN', 'Manipur'],
  ['MP', 'Madhya Pradesh'],
  ['MZ', 'Mizoram'],
  ['NL', 'Nagaland'],
  ['OD', 'Odisha'],
  ['PB', 'Punjab'],
  ['PY', 'Puducherry'],
  ['RJ', 'Rajasthan'],
  ['SK', 'Sikkim'],
  ['TN', 'Tamil Nadu'],
  ['TS', 'Telangana'],
  ['TR', 'Tripura'],
  ['UK', 'Uttarakhand'],
  ['UP', 'Uttar Pradesh'],
  ['WB', 'West Bengal'],
];

// The Store API's cart/checkout responses only give us enabled payment
// gateway IDs (cart.payment_methods = ["cashfree", "cod"]), not display
// titles — the friendly names WooCommerce shows in wp-admin aren't part
// of the public REST schema. So we hardcode labels for the gateways we
// know about and fall back to a title-cased version of the ID for
// anything else, rather than hardcoding which gateways exist at all.
const PAYMENT_METHOD_LABELS = {
  cashfree: 'Cashfree — Card / Net Banking / Wallet / UPI',
  cod: 'Cash on Delivery (testing only)',
  bacs: 'Direct Bank Transfer',
  cheque: 'Cheque Payment',
  paypal: 'PayPal',
};

const paymentMethodLabel = (id) =>
  PAYMENT_METHOD_LABELS[id] ||
  id.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const Checkout = () => {
  const { cart, cartLoading, refreshCart } = useCart();
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [giftNote, setGiftNote] = useState('');
  const [addGift, setAddGift] = useState(false);
  const [sameAsBilling, setSameAsBilling] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const availablePaymentMethods = cart?.payment_methods || [];

  // Default to the first available gateway once the cart loads, and keep
  // the selection valid if the enabled-gateway list changes underneath us
  // (e.g. a gateway gets disabled in wp-admin mid-session).
  useEffect(() => {
    if (
      availablePaymentMethods.length > 0 &&
      !availablePaymentMethods.includes(paymentMethod)
    ) {
      setPaymentMethod(availablePaymentMethods[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availablePaymentMethods.join(',')]);

  // Empty cart guard
  if (cartLoading) {
    return (
      <main className="container-lux py-32 text-center">
        <Loader2 className="w-6 h-6 animate-spin inline text-[#1a120b]/50" />
      </main>
    );
  }

  if (!cart || (cart.items_count ?? 0) === 0) {
    return (
      <main className="container-lux py-32 text-center">
        <p className="font-display text-4xl mb-4">Nothing to complete</p>
        <Link to="/shop" className="btn-primary">
          Explore the House
        </Link>
      </main>
    );
  }

  const totals = cart.totals || {};
  const items = cart.items || [];

  const buildAddresses = (values) => {
    const billing = {
      first_name: values.firstName,
      last_name: values.lastName,
      email: values.email,
      phone: values.phone,
      address_1: values.address,
      address_2: values.address2,
      city: values.city,
      state: values.state,
      postcode: values.pin,
      country: values.country,
    };
    const shipping = sameAsBilling
      ? { ...billing }
      : {
          first_name: values.firstName,
          last_name: values.lastName,
          address_1: values.address,
          address_2: values.address2,
          city: values.city,
          state: values.state,
          postcode: values.pin,
          country: values.country,
        };
    return { billing, shipping };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!paymentMethod) {
      toast.error('No payment method selected.');
      return;
    }

    // Read the form's actual DOM values at submit time rather than trusting
    // only React state. Browser autofill can populate an input's visible
    // value without firing React's onChange, leaving state stale (usually
    // empty) even though the field looks filled — that previously let an
    // empty email slip through and fail WooCommerce's billing-only email
    // validation ("Invalid parameter(s): billing_address"). FormData
    // reflects whatever the browser actually has in the field right now,
    // autofilled or typed, so it can't get out of sync like this.
    const formData = new FormData(e.currentTarget);
    const freshValues = { ...form };
    for (const key of Object.keys(initialForm)) {
      const domValue = formData.get(key);
      if (domValue !== null) freshValues[key] = domValue;
    }
    setForm(freshValues);

    setSubmitting(true);
    try {
      const { billing, shipping } = buildAddresses(freshValues);

      // 1. Push billing + shipping into the cart session (so shipping rates
      //    are calculated for the destination address).
      await updateCartCustomer({
        billing_address: billing,
        shipping_address: shipping,
      });

      // 2. Refresh the cart to read the available shipping rates.
      const refreshed = await refreshCart();
      const firstRate = refreshed?.shipping_rates?.[0];
      if (firstRate?.rate_id) {
        await selectShippingRate(firstRate.rate_id);
      } else {
        // No shipping rates — either all items are virtual or the address
        // is unsupported. Continue anyway; the Store API will tell us if
        // checkout can't proceed.
      }

      // 3. Place the order. The Store API creates the WC order and calls
      //    the gateway's process_payment(). For Cashfree, the gateway
      //    returns a redirect URL to its hosted flow.
      const result = await placeOrder({
        billing_address: billing,
        shipping_address: shipping,
        payment_method: paymentMethod,
        customer_note: addGift ? giftNote : undefined,
      });

      // 4. Hand off to the gateway. Cashfree redirects to a WP-hosted
      //    page that loads the Cashfree JS; after payment, the WP
      //    thank-you page is configured (via mu-plugin) to redirect back
      //    to /order-received/{id}.
      const redirect = result?.payment_result?.redirect_url;
      if (redirect) {
        window.location.href = redirect;
        return;
      }

      if (result?.order_id) {
        const key = result.order_key || '';
        const emailParam = encodeURIComponent(billing.email || '');
        navigate(`/order-received/${result.order_id}?key=${key}&billing_email=${emailParam}`);
        return;
      }

      toast.error('Order placed but no confirmation. Please contact support.');
    } catch (err) {
      // queryClient mutation onError already toasts; log for debugging.
      // eslint-disable-next-line no-console
      console.error('Checkout failed:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  return (
    <main className="container-lux py-14">
      <div className="mb-10">
        <p className="tracking-luxe text-[#b78d3c] mb-3">Complete</p>
        <h1 className="font-display text-5xl md:text-6xl">Checkout</h1>
      </div>

      <form onSubmit={handleSubmit} className="grid lg:grid-cols-[1fr_420px] gap-12">
        <div className="space-y-10">
          <div>
            <h2 className="font-display text-2xl mb-4">Contact</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <input
                required
                name="firstName"
                placeholder="First name"
                value={form.firstName}
                onChange={onChange}
                className="bg-[#f6efe1] border border-[#1a120b]/25 px-5 py-3.5 focus:outline-none focus:border-[#1a120b]"
              />
              <input
                required
                name="lastName"
                placeholder="Last name"
                value={form.lastName}
                onChange={onChange}
                className="bg-[#f6efe1] border border-[#1a120b]/25 px-5 py-3.5 focus:outline-none focus:border-[#1a120b]"
              />
              <input
                required
                name="email"
                type="email"
                placeholder="Email"
                value={form.email}
                onChange={onChange}
                className="bg-[#f6efe1] border border-[#1a120b]/25 px-5 py-3.5 focus:outline-none focus:border-[#1a120b]"
              />
              <input
                required
                name="phone"
                type="tel"
                placeholder="Phone"
                value={form.phone}
                onChange={onChange}
                className="bg-[#f6efe1] border border-[#1a120b]/25 px-5 py-3.5 focus:outline-none focus:border-[#1a120b]"
              />
            </div>
          </div>

          <div>
            <h2 className="font-display text-2xl mb-4">Delivery</h2>
            <div className="space-y-4">
              <input
                required
                name="address"
                placeholder="Address"
                value={form.address}
                onChange={onChange}
                className="w-full bg-[#f6efe1] border border-[#1a120b]/25 px-5 py-3.5 focus:outline-none focus:border-[#1a120b]"
              />
              <input
                name="address2"
                placeholder="Apartment, suite, etc. (optional)"
                value={form.address2}
                onChange={onChange}
                className="w-full bg-[#f6efe1] border border-[#1a120b]/25 px-5 py-3.5 focus:outline-none focus:border-[#1a120b]"
              />
              <div className="grid md:grid-cols-3 gap-4">
                <input
                  required
                  name="city"
                  placeholder="City"
                  value={form.city}
                  onChange={onChange}
                  className="bg-[#f6efe1] border border-[#1a120b]/25 px-5 py-3.5 focus:outline-none focus:border-[#1a120b]"
                />
                <select
                  required
                  name="state"
                  value={form.state}
                  onChange={onChange}
                  className="bg-[#f6efe1] border border-[#1a120b]/25 px-5 py-3.5 focus:outline-none focus:border-[#1a120b]"
                >
                  <option value="" disabled>
                    State
                  </option>
                  {INDIA_STATES.map(([code, name]) => (
                    <option key={code} value={code}>
                      {name}
                    </option>
                  ))}
                </select>
                <input
                  required
                  name="pin"
                  placeholder="PIN"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  title="6-digit PIN code"
                  value={form.pin}
                  onChange={onChange}
                  className="bg-[#f6efe1] border border-[#1a120b]/25 px-5 py-3.5 focus:outline-none focus:border-[#1a120b]"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="flex items-center gap-3 cursor-pointer mb-3">
              <input
                type="checkbox"
                checked={addGift}
                onChange={(e) => setAddGift(e.target.checked)}
                className="w-4 h-4"
              />
              <Gift className="w-4 h-4 text-[#b78d3c]" />
              <span className="font-medium">Add a handwritten card</span>
              <span className="text-xs text-[#1a120b]/50">complimentary</span>
            </label>
            {addGift && (
              <textarea
                rows={3}
                placeholder="Your message…"
                value={giftNote}
                onChange={(e) => setGiftNote(e.target.value)}
                className="w-full bg-[#f6efe1] border border-[#1a120b]/25 px-5 py-3.5 focus:outline-none focus:border-[#1a120b]"
              />
            )}
          </div>

          <div>
            <h2 className="font-display text-2xl mb-4">Payment</h2>
            {availablePaymentMethods.length === 0 ? (
              <p className="flex items-start gap-2 text-sm text-[#1a120b]/70 bg-[#efe6d3] p-4">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#b78d3c]" />
                No payment method is available right now — please contact us
                to place your order.
              </p>
            ) : (
              <div className="space-y-2">
                {availablePaymentMethods.map((id) => (
                  <label
                    key={id}
                    className={`flex items-center gap-3 border p-4 cursor-pointer ${
                      paymentMethod === id
                        ? 'border-[#1a120b] bg-[#efe6d3]'
                        : 'border-[#1a120b]/25'
                    }`}
                  >
                    <input
                      type="radio"
                      name="pay"
                      checked={paymentMethod === id}
                      onChange={() => setPaymentMethod(id)}
                    />
                    <span>{paymentMethodLabel(id)}</span>
                  </label>
                ))}
              </div>
            )}
            <p className="flex items-center gap-2 text-xs text-[#1a120b]/50 mt-4">
              <Lock className="w-3.5 h-3.5" /> Secure checkout
              {paymentMethod === 'cashfree' && " · you'll complete payment on Cashfree's secure page"}.
            </p>
            {paymentMethod === 'cashfree' && (
              <p className="flex items-start gap-2 text-xs text-[#1a120b]/60 mt-3 bg-[#efe6d3] p-3">
                <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-[#b78d3c]" />
                <span>
                  After clicking <strong>Place Order</strong>, you'll be sent to Cashfree to complete
                  payment. Once done, you'll be brought back to a confirmation page.
                </span>
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={submitting || !paymentMethod}
            className="btn-gold w-full text-base disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Placing order…
              </>
            ) : (
              <>
                Place Order {formatPrice(totals.total_price || '0')}
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>

        <aside className="lg:sticky lg:top-28 h-fit">
          <div className="bg-[#1a120b] text-[#f6efe1] p-7">
            <p className="tracking-luxe text-[#b78d3c] mb-2">Order</p>
            <h3 className="font-display text-2xl mb-5">
              Your Bag ({items.length})
            </h3>

            <ul className="space-y-3 max-h-72 overflow-y-auto pr-2 mb-5">
              {items.map((item) => {
                const img = item.images?.[0]?.thumbnail || item.images?.[0]?.src;
                const currency = item.prices?.currency_code || 'INR';
                const flavors = buildBoxFlavors(item);
                return (
                  <li key={item.key} className="flex gap-3 items-start">
                    <div className="w-12 h-12 rounded-full overflow-hidden bg-[#f6efe1]/8 flex items-center justify-center flex-shrink-0">
                      {img ? (
                        <img
                          src={img}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-xs">R</span>
                      )}
                    </div>
                    <div className="flex-1 text-sm">
                      <p className="leading-tight">{item.name}</p>
                      {flavors?.length > 0 ? (
                        <p className="text-xs text-[#f6efe1]/50">
                          {flavors.map((f) => `${f.count}× ${f.name}`).join(', ')}
                        </p>
                      ) : (
                        <p className="text-xs text-[#f6efe1]/50">
                          × {item.quantity}
                        </p>
                      )}
                    </div>
                    <p className="text-sm">
                      {formatPrice(
                        item.totals?.line_total || item.prices?.price,
                        currency
                      )}
                    </p>
                  </li>
                );
              })}
            </ul>

            <div className="space-y-2 text-sm border-t border-[#f6efe1]/12 pt-4">
              <div className="flex justify-between">
                <span className="text-[#f6efe1]/70">Subtotal</span>
                <span>{formatPrice(totals.total_items || '0')}</span>
              </div>
              {parseInt(totals.total_discount || '0', 10) > 0 && (
                <div className="flex justify-between text-[#b78d3c]">
                  <span>Discount</span>
                  <span>− {formatPrice(totals.total_discount || '0')}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-[#f6efe1]/70">Shipping</span>
                <span>
                  {parseInt(totals.total_shipping || '0', 10) === 0
                    ? 'Complimentary'
                    : formatPrice(totals.total_shipping || '0')}
                </span>
              </div>
              <div className="gold-rule my-3"></div>
              <div className="flex justify-between font-display text-2xl">
                <span>Total</span>
                <span>{formatPrice(totals.total_price || '0')}</span>
              </div>
            </div>
          </div>

          <div className="mt-4 text-xs text-[#1a120b]/60 flex items-center gap-2">
            <MapPin className="w-3.5 h-3.5" /> Composed and dispatched from Mumbai
          </div>
        </aside>
      </form>
    </main>
  );
};

export default Checkout;
